import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-community";
import CellTextDefault from "../Table/components/CellDefault";
import { ColumnDef, ReceivedProps } from "../Table/hook";
import CellEditRenderer from "./components/CellEditRenderer";
import type { ValidationError } from "./types";
import isEmpty from "lodash/isEmpty";
import { useConfirmRedirect } from "@lhd/hooks";

type TableFormColumnDef = ColumnDef & ColDef;
export type DataChanged = Record<string, Record<string, any>>;
export type DataValidation = Record<string, Record<string, ValidationError>>;
export type ReceivedPropsForForm = ReceivedProps & {
  getRowIdForForm?: (data: any) => any;
  createNewRow?: (id: string, index: number) => Record<string, any>;
  handleSetStateForm?: (state: {
    hasChanged: boolean;
    hasError: boolean;
    hasUnsavedChanges: boolean;
  }) => void;
};
const useTable = (props: ReceivedPropsForForm) => {
  const { isEditMode, columnDefs, handleSetStateForm } = props;
  const dataChangedRef = useRef<DataChanged>({});
  const dataValidationRef = useRef<DataValidation>({});
  const nextNewRowIdRef = useRef(0);
  const [newRows, setNewRows] = useState<Record<string, any>[]>([]);
  const [forceRender, setForceRender] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useConfirmRedirect(hasUnsavedChanges);

  const addRows = useCallback(
    (count: number) => {
      if (!Number.isSafeInteger(count) || count <= 0) return;

      const existingIds = new Set(
        [...props.rowData, ...newRows].map((row) =>
          String(props.getRowIdForForm ? props.getRowIdForForm(row) : row.id),
        ),
      );
      const rows = Array.from({ length: count }, (_, index) => {
        let id: string;
        do {
          id = `__table_form_new_row_${nextNewRowIdRef.current++}`;
        } while (existingIds.has(id));

        const row = props.createNewRow?.(id, index) ?? { id };
        const rowId = props.getRowIdForForm
          ? props.getRowIdForForm(row)
          : row.id;
        if (rowId === undefined || rowId === null || rowId === "") {
          throw new Error("TableForm: a new row must have a row ID");
        }
        const resolvedId = String(rowId);
        if (existingIds.has(resolvedId)) {
          throw new Error(`TableForm: duplicate new row ID ${resolvedId}`);
        }
        existingIds.add(resolvedId);
        return row;
      });

      setNewRows((currentRows) => [...currentRows, ...rows]);
    },
    [props.rowData, props.getRowIdForForm, props.createNewRow, newRows],
  );

  const rowData = useMemo(
    () => [...props.rowData, ...newRows],
    [props.rowData, newRows],
  );

  const context = useMemo(
    () => ({
      ...(props.context || {}),
      dataChangedRef,
      dataValidationRef,
      handleForceRender: () => setForceRender({}),
    }),
    [props.context],
  );

  const columns = useMemo(() => {
    const addCellRenderer = (
      column: TableFormColumnDef,
    ): TableFormColumnDef => {
      if (column.children) {
        return {
          ...column,
          children: column.children.map(addCellRenderer),
        };
      }
      const ReadOnlyCellRenderer = column.cellRenderer ?? CellTextDefault;
      let cellRenderer = ReadOnlyCellRenderer;
      if (isEditMode && column.editInfo) {
        const getDisabledData = column.editInfo.getDisabledData;
        cellRenderer = getDisabledData
          ? (params: any) =>
              getDisabledData(params.data) ? (
                <ReadOnlyCellRenderer {...params} />
              ) : (
                <CellEditRenderer {...params} />
              )
          : CellEditRenderer;
      }

      return {
        ...column,
        getRowIdForForm: props.getRowIdForForm,
        cellRenderer,
      };
    };

    return columnDefs.map(addCellRenderer);
  }, [isEditMode, columnDefs, props.getRowIdForForm]);

  const getChangedData = useCallback(() => dataChangedRef.current, []);
  const resetChangedData = useCallback(() => {
    dataChangedRef.current = {};
    dataValidationRef.current = {};
    setForceRender({});
  }, []);
  const discardChanges = useCallback(() => {
    resetChangedData();
    setNewRows([]);
  }, [resetChangedData]);

  useEffect(() => {
    const hasChanged = !isEmpty(dataChangedRef.current);
    const hasError = !isEmpty(dataValidationRef.current);
    const nextHasUnsavedChanges = hasChanged || newRows.length > 0;

    setHasUnsavedChanges(nextHasUnsavedChanges);
    handleSetStateForm?.({
      hasChanged,
      hasError,
      hasUnsavedChanges: nextHasUnsavedChanges,
    });
  }, [forceRender, newRows.length]);

  return {
    ...props,
    rowData,
    context,
    columnDefs: columns,
    dataChangedRef,
    getChangedData,
    resetChangedData,
    discardChanges,
    addRows,
  };
};

export type Props = ReturnType<typeof useTable>;

export default useTable;
